import {
  forwardRef,
  useMemo,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';

import { EyeIcon, EyeOffIcon } from '@tankef/icons';

import { Input } from '../../primitives/Input';
import { cn } from '../../utils';

import { PasswordRulesList, type PasswordRulesListProps } from './PasswordRulesList';
import { PasswordStrengthMeter, type PasswordStrengthMeterProps } from './PasswordStrengthMeter';

export type PasswordIndicator = 'none' | 'rules' | 'strength' | 'both';

export interface PasswordFieldProps {
  value: string;
  onChange: (value: string) => void;
  name?: string;
  id?: string;
  label?: string;
  placeholder?: string;
  autoComplete?: 'current-password' | 'new-password' | 'off';
  disabled?: boolean;
  required?: boolean;

  /**
   * Which feedback to show. Defaults to `rules` (no meter) for
   * change-password / login flows; use `both` for registration/reset where
   * UX guidance on strength adds real value.
   */
  indicator?: PasswordIndicator;
  /** i18n-resolved labels for the rules list. */
  rulesLabels?: PasswordRulesListProps['labels'];
  /** i18n-resolved labels for the strength meter + hints. */
  strengthLabels?: PasswordStrengthMeterProps['labels'];
  hintLabels?: PasswordStrengthMeterProps['hintLabels'];

  /** Error message from form validation. Only shown after submit / blur. */
  error?: string;
  /** Show show/hide toggle. Default: true. */
  showToggle?: boolean;
  /** Caps Lock warning text (i18n-resolved). If omitted, warning is not rendered. */
  capsLockWarning?: string;

  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  className?: string;
}

/**
 * Input para contraseña con feedback UX:
 *   1. Toggle show/hide (accesible, con iconos).
 *   2. Caps Lock warning (detectado en keypress, no intrusivo).
 *   3. Checklist de reglas + medidor de fortaleza (opt-in via `indicator`).
 *   4. Error solo después de submit/blur — mientras escribe sólo guía, nunca castiga.
 *
 * Los labels llegan resueltos (i18n en el caller) para mantener el paquete
 * agnóstico al idioma y al framework de traducción.
 */
export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(function PasswordField(
  {
    value,
    onChange,
    name,
    id,
    label,
    placeholder,
    autoComplete = 'new-password',
    disabled,
    required,
    indicator = 'rules',
    rulesLabels,
    strengthLabels,
    hintLabels,
    error,
    showToggle = true,
    capsLockWarning,
    onBlur,
    className,
  },
  ref
) {
  const [show, setShow] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  const showRules = indicator === 'rules' || indicator === 'both';
  const showStrength = indicator === 'strength' || indicator === 'both';

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Caps Lock detection only fires on physical key events (not paste).
    if (typeof e.getModifierState === 'function') {
      setCapsLock(e.getModifierState('CapsLock'));
    }
  };

  const rightIcon = useMemo(() => {
    if (!showToggle) return undefined;
    return (
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
        className="flex h-5 w-5 items-center justify-center text-tkf-fg-subtle hover:text-tkf-fg"
      >
        {show ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
      </button>
    );
  }, [show, showToggle]);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Input
        ref={ref}
        id={id}
        name={name}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        label={label}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        required={required}
        error={error}
        rightIcon={rightIcon}
      />

      {capsLock && capsLockWarning && (
        <p className="text-xs text-warning-700" role="status">
          {capsLockWarning}
        </p>
      )}

      {showStrength && strengthLabels && (
        <PasswordStrengthMeter
          password={value}
          labels={strengthLabels}
          hintLabels={hintLabels}
          showHint
        />
      )}

      {showRules && rulesLabels && <PasswordRulesList password={value} labels={rulesLabels} />}
    </div>
  );
});
